// src/services/forumApi.js
import axios from 'axios';
import { getJwtToken } from '../utils/auth';
import { getBaseUrl } from "../utils/apiClient";

const forumApiClient = axios.create({
    baseURL: `${getBaseUrl()}/forum`,
});

forumApiClient.interceptors.request.use(async (config) => {
    const token = await getJwtToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Agregar headers para CORS
    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept'] = 'application/json';
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Interceptor para manejo de errores CORS
forumApiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        console.error('Forum API Error:', error);

        // Log de debugging para CORS
        if (error.config) {
            console.log('Request URL:', error.config.url);
            console.log('Request Method:', error.config.method);
            console.log('Base URL:', error.config.baseURL);
        }

        if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error') || error.message.includes('CORS')) {
            console.error('❌ CORS Error detected:', {
                url: error.config?.url,
                baseURL: error.config?.baseURL,
                method: error.config?.method,
                headers: error.config?.headers
            });

            const corsError = new Error(`
                🚨 Error de CORS detectado:
                
                URL: ${error.config?.baseURL}${error.config?.url}
                
                Posibles soluciones:
                1. Verificar que el backend esté corriendo en puerto 8081
                2. Comprobar configuración CORS en el servidor
                3. Verificar que las rutas /forum/* estén habilitadas
                4. Confirmar headers Access-Control-Allow-Origin
                
                Para desarrollo: Considera usar un proxy o deshabilitar CORS temporalmente.
            `);
            corsError.isCorsError = true;
            corsError.originalError = error;
            return Promise.reject(corsError);
        }
        return Promise.reject(error);
    }
);

// Crear un nuevo post
export const createPost = async (postData) => {
    try {
        const response = await forumApiClient.post('/posts', postData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Obtener posts por categoría y ubicación
export const getPostsByCategory = async (categoryId, serviceLocationId, limit = 20, urgentOnly = false) => {
    try {
        const params = {};

        // Filtros principales según documentación
        if (urgentOnly) {
            params.urgentOnly = true;
        } else if (categoryId) {
            params.categoryId = categoryId;
        }

        // Ubicación (opcional)
        if (serviceLocationId) {
            params.serviceLocationId = serviceLocationId;
        }

        // Agregar limit solo si es un número válido
        if (limit && typeof limit === 'number' && limit > 0) {
            params.limit = limit;
        }

        const response = await forumApiClient.get('/posts', { params });

        // El backend devuelve directamente {posts: [], total: number}
        return response.data;
    } catch (error) {
        console.error('❌ Error en getPostsByCategory:', error);
        throw error;
    }
};

// Obtener un post por ID
export const getPostById = async (postId, incrementViews = true) => {
    try {
        const response = await forumApiClient.get(`/posts/${postId}`, {
            params: { incrementViews }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Obtener posts solo por categoría (cualquier ubicación)
export const getPostsByCategoryOnly = async (categoryId, limit = 20) => {
    try {
        const params = { categoryId };
        if (limit) params.limit = limit;

        const response = await forumApiClient.get('/posts', { params });
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo posts por categoría:', error);
        throw error;
    }
};

// Obtener posts urgentes
export const getUrgentPosts = async (limit = 20) => {
    try {
        const params = { urgentOnly: true };
        if (limit) params.limit = limit;

        const response = await forumApiClient.get('/posts', { params });
        return response.data;
    } catch (error) {
        console.error('❌ Error obteniendo posts urgentes:', error);
        throw error;
    }
};

// Obtener posts de un usuario
export const getUserPosts = async () => {
    try {
        const response = await forumApiClient.get(`/posts/user`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Proveedor expresa interés en un post
export const addInterestToPost = async (postId, interestData) => {
    try {
        const response = await forumApiClient.post(`/posts/${postId}/interest`, interestData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Actualizar estado del post
export const updatePostStatus = async (postId, statusData) => {
    try {
        const response = await forumApiClient.put(`/posts/${postId}/status`, statusData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Eliminar post
export const deletePost = async (postId) => {
    try {
        const response = await forumApiClient.delete(`/posts/${postId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Función de test para debuggear problemas con el backend
export const testForumConnection = async () => {
    try {
        // Test básico
        const response = await forumApiClient.get('/posts', { params: { limit: 1 } });
        console.log('✅ Conexión exitosa con el forum API');
        console.log('📦 Estructura de respuesta:', {
            posts: Array.isArray(response.data?.posts),
            total: typeof response.data?.total,
            postsLength: response.data?.posts?.length
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error de conexión:', error.response?.status, error.message);
        throw error;
    }
};

// Para debugging desde la consola del navegador
window.testForumAPI = {
    testConnection: testForumConnection,
    getPostsByCategory,
    getPostsByCategoryOnly,
    getUrgentPosts
};
